import { DestroyRef, DOCUMENT, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  Event as RouterEvent,
} from '@angular/router';
import { formatUrl, getRouteUrl } from '@common';
import { CONFIG } from '@config';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { SeoData } from '../models/seo/seo-data.interface';
import { SEO_CONFIG } from '../tokens/seo-config.token';
import {
  formatMetaTags,
  formatMetaTagsAbsoluteUrl,
} from '../utils/meta-tags/meta-tags.utils';
import { formatSeoData } from '../utils/seo/seo.utils';

@Service()
export class SeoService {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly config = inject(CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly metaTagService = inject(Meta);
  private readonly router = inject(Router);
  private readonly seoConfig = inject(SEO_CONFIG);
  private readonly titleService = inject(Title);

  private readonly clientName = this.config.clientName;

  setPageTitle(title?: string): void {
    if (!title) {
      this.titleService.setTitle(
        `${this.clientName} Registration - Activate Your Guarantee`,
      );
      return;
    }

    this.titleService.setTitle(`${title}`);
  }

  setSeoData(): void {
    const data: SeoData = {
      ...formatSeoData(this.seoConfig),
    };

    let metaTags: MetaDefinition[] = formatMetaTags(data);
    metaTags = formatMetaTagsAbsoluteUrl(metaTags, this.seoConfig);

    this.setMetaTags(...metaTags);
    this.setPageTitle(data.title);
  }

  setCanonicalUrlOnNavigationEnd(): void {
    this.router.events
      .pipe(
        filter(
          (event: RouterEvent): event is NavigationEnd =>
            event instanceof NavigationEnd,
        ),
        map(() => getRouteUrl(this.activatedRoute.snapshot)),
        map((routeUrl: string) =>
          formatUrl(`${this.seoConfig.baseUrl}/${routeUrl}`),
        ),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((canonicalUrl: string) => this.setCanonicalUrl(canonicalUrl));
  }

  private addWwwPrefix(url: string): string {
    const urlObject = new URL(url);
    if (!urlObject.hostname.startsWith('www.')) {
      urlObject.hostname = 'www.' + urlObject.hostname;
    }
    return urlObject.toString();
  }

  private setCanonicalUrl(canonicalUrl: string): void {
    this.document.querySelector(`link[rel='canonical']`)?.remove();

    const wwwCanonicalUrl = this.addWwwPrefix(canonicalUrl);

    const link = this.document.createElement('link');
    link.rel = 'canonical';
    link.href = wwwCanonicalUrl;

    this.setMetaTags({ property: 'og:url', content: wwwCanonicalUrl });
    this.document.head.appendChild(link);
  }

  setMetaTags(...metaTags: MetaDefinition[]): void {
    metaTags.forEach((metaTag: MetaDefinition) => {
      this.metaTagService.removeTag(`property="${metaTag.property}"`);
    });

    this.metaTagService.addTags(metaTags);
  }
}
